import { configureStore, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { useSelector, useDispatch, TypedUseSelectorHook } from 'react-redux';
import { Infer } from 'spacetimedb';
import UserRow from '../module_bindings_control/user_table';
import CoinTransactionRow from '../module_bindings_control/coin_transaction_table';
import PurchaseHistoryRow from '../module_bindings_control/purchase_history_table';
import ActiveMatchDirectoryRow from '../module_bindings_control/active_match_directory_table';
import GameRecordRow from '../module_bindings_control/game_record_table';
import GlobalChatMessageRow from '../module_bindings_control/global_chat_message_table';
import ChatRateLimitRow from '../module_bindings_control/chat_rate_limit_table';
import MatchmakingQueueRow from '../module_bindings_control/matchmaking_queue_table';
import StoreItemRow from '../module_bindings_control/store_item_table';
import UserSubscriptionRow from '../module_bindings_control/user_subscription_table';
import RankedSeasonRow from '../module_bindings_control/ranked_season_table';
import AchievementRow from '../module_bindings_control/achievement_table';
import StoreBundleRow from '../module_bindings_control/store_bundle_table';
import BundleItemRow from '../module_bindings_control/bundle_item_table';
import StorePromoRow from '../module_bindings_control/store_promo_table';

export type User = Infer<typeof UserRow>;
export type CoinTransaction = Infer<typeof CoinTransactionRow>;
export type PurchaseHistory = Infer<typeof PurchaseHistoryRow>;
export type ActiveMatchDirectory = Infer<typeof ActiveMatchDirectoryRow>;
export type GameRecord = Infer<typeof GameRecordRow>;
export type GlobalChatMessage = Infer<typeof GlobalChatMessageRow>;
export type ChatRateLimit = Infer<typeof ChatRateLimitRow>;
export type MatchmakingQueue = Infer<typeof MatchmakingQueueRow>;
export type StoreItem = Infer<typeof StoreItemRow>;
export type UserSubscription = Infer<typeof UserSubscriptionRow>;
export type RankedSeason = Infer<typeof RankedSeasonRow>;
export type Achievement = Infer<typeof AchievementRow>;
export type StoreBundle = Infer<typeof StoreBundleRow>;
export type BundleItem = Infer<typeof BundleItemRow>;
export type StorePromo = Infer<typeof StorePromoRow>;

type ConnectionStatus = 'disconnected' | 'connecting' | 'connected' | 'error';

interface AdminState {
  authenticated: boolean;
  adminIdentity: string;
  jwt: string;
  connectionStatus: ConnectionStatus;
}

interface DataState {
  users: User[];
  coinTransactions: CoinTransaction[];
  purchaseHistory: PurchaseHistory[];
  activeMatches: ActiveMatchDirectory[];
  gameRecords: GameRecord[];
  globalChat: GlobalChatMessage[];
  chatRateLimits: ChatRateLimit[];
  matchmakingQueue: MatchmakingQueue[];
  storeItems: StoreItem[];
  storeBundles: StoreBundle[];
  bundleItems: BundleItem[];
  storePromos: StorePromo[];
  userSubscriptions: UserSubscription[];
  rankedSeasons: RankedSeason[];
  achievements: Achievement[];
}

const adminSlice = createSlice({
  name: 'admin',
  initialState: {
    authenticated: false,
    adminIdentity: '',
    jwt: '',
    connectionStatus: 'disconnected',
  } as AdminState,
  reducers: {
    setAuthenticated(state, action: PayloadAction<boolean>) {
      state.authenticated = action.payload;
    },
    setAdminIdentity(state, action: PayloadAction<string>) {
      state.adminIdentity = action.payload;
    },
    setJwt(state, action: PayloadAction<string>) {
      state.jwt = action.payload;
    },
    setConnectionStatus(state, action: PayloadAction<ConnectionStatus>) {
      state.connectionStatus = action.payload;
    },
    logout(state) {
      state.authenticated = false;
      state.adminIdentity = '';
      state.jwt = '';
      state.connectionStatus = 'disconnected';
    },
  },
});

const dataSlice = createSlice({
  name: 'data',
  initialState: {
    users: [],
    coinTransactions: [],
    purchaseHistory: [],
    activeMatches: [],
    gameRecords: [],
    globalChat: [],
    chatRateLimits: [],
    matchmakingQueue: [],
    storeItems: [],
    storeBundles: [],
    bundleItems: [],
    storePromos: [],
    userSubscriptions: [],
    rankedSeasons: [],
    achievements: [],
  } as DataState,
  reducers: {
    // Users
    setUsers(state, action: PayloadAction<User[]>) { state.users = action.payload; },
    upsertUser(state, action: PayloadAction<User>) {
      const idx = state.users.findIndex(u => u.id === action.payload.id);
      if (idx >= 0) state.users[idx] = action.payload; else state.users.push(action.payload);
    },
    removeUser(state, action: PayloadAction<User>) {
      state.users = state.users.filter(u => u.id !== action.payload.id);
    },

    // Coin Transactions
    setCoinTransactions(state, action: PayloadAction<CoinTransaction[]>) { state.coinTransactions = action.payload; },
    upsertCoinTransaction(state, action: PayloadAction<CoinTransaction>) {
      const idx = state.coinTransactions.findIndex(t => t.id === action.payload.id);
      if (idx >= 0) state.coinTransactions[idx] = action.payload; else state.coinTransactions.push(action.payload);
    },
    removeCoinTransaction(state, action: PayloadAction<CoinTransaction>) {
      state.coinTransactions = state.coinTransactions.filter(t => t.id !== action.payload.id);
    },

    // Purchase History
    setPurchaseHistory(state, action: PayloadAction<PurchaseHistory[]>) { state.purchaseHistory = action.payload; },
    upsertPurchaseHistory(state, action: PayloadAction<PurchaseHistory>) {
      const idx = state.purchaseHistory.findIndex(p => p.id === action.payload.id);
      if (idx >= 0) state.purchaseHistory[idx] = action.payload; else state.purchaseHistory.push(action.payload);
    },
    removePurchaseHistory(state, action: PayloadAction<PurchaseHistory>) {
      state.purchaseHistory = state.purchaseHistory.filter(p => p.id !== action.payload.id);
    },

    // Active Matches
    setActiveMatches(state, action: PayloadAction<ActiveMatchDirectory[]>) { state.activeMatches = action.payload; },
    upsertActiveMatch(state, action: PayloadAction<ActiveMatchDirectory>) {
      const idx = state.activeMatches.findIndex(m => m.matchId === action.payload.matchId);
      if (idx >= 0) state.activeMatches[idx] = action.payload; else state.activeMatches.push(action.payload);
    },
    removeActiveMatch(state, action: PayloadAction<ActiveMatchDirectory>) {
      state.activeMatches = state.activeMatches.filter(m => m.matchId !== action.payload.matchId);
    },

    // Game Records
    setGameRecords(state, action: PayloadAction<GameRecord[]>) { state.gameRecords = action.payload; },
    upsertGameRecord(state, action: PayloadAction<GameRecord>) {
      const idx = state.gameRecords.findIndex(g => g.id === action.payload.id);
      if (idx >= 0) state.gameRecords[idx] = action.payload; else state.gameRecords.push(action.payload);
    },
    removeGameRecord(state, action: PayloadAction<GameRecord>) {
      state.gameRecords = state.gameRecords.filter(g => g.id !== action.payload.id);
    },

    // Global Chat
    setGlobalChat(state, action: PayloadAction<GlobalChatMessage[]>) { state.globalChat = action.payload; },
    upsertGlobalChat(state, action: PayloadAction<GlobalChatMessage>) {
      const idx = state.globalChat.findIndex(m => m.id === action.payload.id);
      if (idx >= 0) state.globalChat[idx] = action.payload; else state.globalChat.push(action.payload);
    },
    removeGlobalChat(state, action: PayloadAction<GlobalChatMessage>) {
      state.globalChat = state.globalChat.filter(m => m.id !== action.payload.id);
    },

    // Chat Rate Limits
    setChatRateLimits(state, action: PayloadAction<ChatRateLimit[]>) { state.chatRateLimits = action.payload; },
    upsertChatRateLimit(state, action: PayloadAction<ChatRateLimit>) {
      const idx = state.chatRateLimits.findIndex(r => r.userId === action.payload.userId);
      if (idx >= 0) state.chatRateLimits[idx] = action.payload; else state.chatRateLimits.push(action.payload);
    },
    removeChatRateLimit(state, action: PayloadAction<ChatRateLimit>) {
      state.chatRateLimits = state.chatRateLimits.filter(r => r.userId !== action.payload.userId);
    },

    // Matchmaking Queue
    setMatchmakingQueue(state, action: PayloadAction<MatchmakingQueue[]>) { state.matchmakingQueue = action.payload; },
    upsertMatchmakingQueue(state, action: PayloadAction<MatchmakingQueue>) {
      const idx = state.matchmakingQueue.findIndex(q => q.userId === action.payload.userId);
      if (idx >= 0) state.matchmakingQueue[idx] = action.payload; else state.matchmakingQueue.push(action.payload);
    },
    removeMatchmakingQueue(state, action: PayloadAction<MatchmakingQueue>) {
      state.matchmakingQueue = state.matchmakingQueue.filter(q => q.userId !== action.payload.userId);
    },

    // Store Items
    setStoreItems(state, action: PayloadAction<StoreItem[]>) { state.storeItems = action.payload; },
    upsertStoreItem(state, action: PayloadAction<StoreItem>) {
      const idx = state.storeItems.findIndex(s => s.id === action.payload.id);
      if (idx >= 0) state.storeItems[idx] = action.payload; else state.storeItems.push(action.payload);
    },
    removeStoreItem(state, action: PayloadAction<StoreItem>) {
      state.storeItems = state.storeItems.filter(s => s.id !== action.payload.id);
    },

    // Store Bundles
    setStoreBundles(state, action: PayloadAction<StoreBundle[]>) { state.storeBundles = action.payload; },
    upsertStoreBundle(state, action: PayloadAction<StoreBundle>) {
      const idx = state.storeBundles.findIndex(b => b.id === action.payload.id);
      if (idx >= 0) state.storeBundles[idx] = action.payload; else state.storeBundles.push(action.payload);
    },
    removeStoreBundle(state, action: PayloadAction<StoreBundle>) {
      state.storeBundles = state.storeBundles.filter(b => b.id !== action.payload.id);
    },

    // Bundle Items
    setBundleItems(state, action: PayloadAction<BundleItem[]>) { state.bundleItems = action.payload; },
    upsertBundleItem(state, action: PayloadAction<BundleItem>) {
      const idx = state.bundleItems.findIndex(bi => bi.id === action.payload.id);
      if (idx >= 0) state.bundleItems[idx] = action.payload; else state.bundleItems.push(action.payload);
    },
    removeBundleItem(state, action: PayloadAction<BundleItem>) {
      state.bundleItems = state.bundleItems.filter(bi => bi.id !== action.payload.id);
    },

    // Store Promos
    setStorePromos(state, action: PayloadAction<StorePromo[]>) { state.storePromos = action.payload; },
    upsertStorePromo(state, action: PayloadAction<StorePromo>) {
      const idx = state.storePromos.findIndex(p => p.id === action.payload.id);
      if (idx >= 0) state.storePromos[idx] = action.payload; else state.storePromos.push(action.payload);
    },
    removeStorePromo(state, action: PayloadAction<StorePromo>) {
      state.storePromos = state.storePromos.filter(p => p.id !== action.payload.id);
    },

    // User Subscriptions
    setUserSubscriptions(state, action: PayloadAction<UserSubscription[]>) { state.userSubscriptions = action.payload; },
    upsertUserSubscription(state, action: PayloadAction<UserSubscription>) {
      const idx = state.userSubscriptions.findIndex(s => s.userId === action.payload.userId);
      if (idx >= 0) state.userSubscriptions[idx] = action.payload; else state.userSubscriptions.push(action.payload);
    },
    removeUserSubscription(state, action: PayloadAction<UserSubscription>) {
      state.userSubscriptions = state.userSubscriptions.filter(s => s.userId !== action.payload.userId);
    },

    // Ranked Seasons
    setRankedSeasons(state, action: PayloadAction<RankedSeason[]>) { state.rankedSeasons = action.payload; },
    upsertRankedSeason(state, action: PayloadAction<RankedSeason>) {
      const idx = state.rankedSeasons.findIndex(s => s.id === action.payload.id);
      if (idx >= 0) state.rankedSeasons[idx] = action.payload; else state.rankedSeasons.push(action.payload);
    },
    removeRankedSeason(state, action: PayloadAction<RankedSeason>) {
      state.rankedSeasons = state.rankedSeasons.filter(s => s.id !== action.payload.id);
    },

    // Achievements
    setAchievements(state, action: PayloadAction<Achievement[]>) { state.achievements = action.payload; },
    upsertAchievement(state, action: PayloadAction<Achievement>) {
      const idx = state.achievements.findIndex(a => a.id === action.payload.id);
      if (idx >= 0) state.achievements[idx] = action.payload; else state.achievements.push(action.payload);
    },
    removeAchievement(state, action: PayloadAction<Achievement>) {
      state.achievements = state.achievements.filter(a => a.id !== action.payload.id);
    },
  },
});

export const adminActions = adminSlice.actions;
export const dataActions = dataSlice.actions;

export const store = configureStore({
  reducer: {
    admin: adminSlice.reducer,
    data: dataSlice.reducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false,
    }),
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
export const useAppDispatch = () => useDispatch<AppDispatch>();
